import { useMemo, useState, type FormEvent } from "react";
import { AdminDataTable } from "@/components/admin/AdminDataTable";
import { AdminFormDrawer } from "@/components/admin/AdminFormDrawer";
import { AdminPageHeader } from "@/components/admin/AdminPageHeader";
import { ConfirmDeleteDialog } from "@/components/admin/ConfirmDeleteDialog";
import { EmptyState } from "@/components/admin/EmptyState";
import { StatusBadge } from "@/components/admin/StatusBadge";
import { Button } from "@/components/ui/Button";
import { useCurrentUser } from "@/features/auth/hooks";
import { roleAtLeast } from "@/features/auth/roles";
import {
  useCategories,
  useCreateCategory,
  useDeleteCategory,
  useUpdateCategory,
} from "@/features/portfolio-categories/hooks";
import type { PortfolioCategoryInput, PortfolioCategoryView } from "@/features/portfolio-categories/types";

const emptyForm: PortfolioCategoryInput = {
  name: "",
  slug: "",
  description: "",
  sortOrder: 0,
  isActive: true,
};

export function PortfolioCategoriesPage() {
  const categories = useCategories();
  const currentUser = useCurrentUser();
  const createCategory = useCreateCategory();
  const updateCategory = useUpdateCategory();
  const deleteCategory = useDeleteCategory();
  const [drawerMode, setDrawerMode] = useState<"create" | "edit" | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<PortfolioCategoryView | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PortfolioCategoryView | null>(null);
  const [form, setForm] = useState<PortfolioCategoryInput>(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const canDelete = roleAtLeast(currentUser.data?.role, "ADMIN");

  const sortedCategories = useMemo(
    () => [...(categories.data ?? [])].sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name)),
    [categories.data],
  );

  function openCreate() {
    setSelectedCategory(null);
    setForm(emptyForm);
    setError(null);
    setDrawerMode("create");
  }

  function openEdit(category: PortfolioCategoryView) {
    setSelectedCategory(category);
    setForm({
      name: category.name,
      slug: category.slug,
      description: category.description ?? "",
      sortOrder: category.sortOrder,
      isActive: category.isActive,
    });
    setError(null);
    setDrawerMode("edit");
  }

  function closeDrawer() {
    setDrawerMode(null);
    setSelectedCategory(null);
    setForm(emptyForm);
    setError(null);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!form.name.trim()) {
      setError("Name is required.");
      return;
    }

    if (drawerMode === "create") {
      createCategory.mutate(form, {
        onSuccess: closeDrawer,
        onError: (err) => setError(err instanceof Error ? err.message : "Unable to create category."),
      });
      return;
    }

    if (selectedCategory) {
      updateCategory.mutate(
        { category: selectedCategory, input: form },
        {
          onSuccess: closeDrawer,
          onError: (err) => setError(err instanceof Error ? err.message : "Unable to update category."),
        },
      );
    }
  }

  function confirmDelete() {
    if (!deleteTarget) return;
    deleteCategory.mutate(deleteTarget.id, {
      onSuccess: () => setDeleteTarget(null),
    });
  }

  return (
    <div className="space-y-6">
      <AdminPageHeader
        title="Portfolio Categories"
        description="Manage the category taxonomy used to organize public portfolio work."
        actions={<Button onClick={openCreate}>New Category</Button>}
      />
      {categories.isError && (
        <div className="rounded-md border border-red-300/30 bg-red-500/10 p-4 text-sm text-red-100">
          {categories.error instanceof Error ? categories.error.message : "Unable to load categories."}
        </div>
      )}
      <AdminDataTable
        rows={sortedCategories}
        getRowKey={(category) => category.id}
        emptyState={
          <EmptyState
            title={categories.isLoading ? "Loading categories..." : "No categories yet"}
            description="Create the first portfolio category to start organizing portfolio work."
            action={!categories.isLoading && <Button onClick={openCreate}>Create Category</Button>}
          />
        }
        columns={[
          {
            key: "name",
            header: "Name",
            render: (category) => (
              <div>
                <p className="font-medium text-white">{category.name}</p>
                <p className="mt-1 text-xs text-content-muted">/{category.slug}</p>
              </div>
            ),
          },
          {
            key: "description",
            header: "Description",
            render: (category) => category.description || <span className="text-content-muted">No description</span>,
          },
          {
            key: "sortOrder",
            header: "Sort",
            render: (category) => category.sortOrder,
          },
          {
            key: "status",
            header: "Status",
            render: (category) => (
              <StatusBadge tone={category.isActive ? "active" : "inactive"}>
                {category.isActive ? "Active" : "Deleted"}
              </StatusBadge>
            ),
          },
          {
            key: "items",
            header: "Items",
            render: (category) => category._count?.items ?? 0,
          },
          {
            key: "actions",
            header: "Actions",
            className: "px-4 py-4 align-middle text-right",
            render: (category) => (
              <div className="flex justify-end gap-2">
                <Button size="sm" variant="ghost" onClick={() => openEdit(category)}>
                  Edit
                </Button>
                {canDelete && category.isActive && (
                  <Button size="sm" variant="danger" onClick={() => setDeleteTarget(category)}>
                    Delete
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />
      <AdminFormDrawer
        isOpen={drawerMode !== null}
        title={drawerMode === "create" ? "New Category" : "Edit Category"}
        description="Category changes are used by the public portfolio once portfolio pages are wired."
        onClose={closeDrawer}
      >
        <form className="space-y-5" onSubmit={handleSubmit}>
          <TextField label="Name" value={form.name} onChange={(value) => setForm((current) => ({ ...current, name: value }))} required />
          <TextField label="Slug" value={form.slug ?? ""} onChange={(value) => setForm((current) => ({ ...current, slug: value }))} />
          <label className="block">
            <span className="text-sm text-content-secondary">Description</span>
            <textarea
              className="mt-2 min-h-28 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 py-2 text-sm text-white outline-none transition focus:border-cyan-300"
              value={form.description ?? ""}
              onChange={(event) => setForm((current) => ({ ...current, description: event.target.value }))}
              maxLength={500}
            />
          </label>
          <TextField
            label="Sort Order"
            value={String(form.sortOrder ?? 0)}
            type="number"
            onChange={(value) => setForm((current) => ({ ...current, sortOrder: Number(value) }))}
          />
          <label className="flex items-center gap-3 rounded-md border border-cyan-300/15 bg-black/20 p-3">
            <input
              className="size-4 accent-cyan-300"
              type="checkbox"
              checked={form.isActive}
              disabled={!canDelete && selectedCategory?.isActive === true}
              onChange={(event) => setForm((current) => ({ ...current, isActive: event.target.checked }))}
            />
            <span className="text-sm text-content-secondary">Active on the public site</span>
          </label>
          {!canDelete && (
            <p className="text-xs text-content-muted">Only Admin, Owner, and Developer roles can delete or restore category status.</p>
          )}
          {error && <p className="rounded-md border border-red-300/30 bg-red-500/10 px-3 py-2 text-sm text-red-100">{error}</p>}
          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={closeDrawer}>
              Cancel
            </Button>
            <Button type="submit" disabled={createCategory.isPending || updateCategory.isPending}>
              {createCategory.isPending || updateCategory.isPending ? "Saving..." : "Save Category"}
            </Button>
          </div>
        </form>
      </AdminFormDrawer>
      <ConfirmDeleteDialog
        isOpen={deleteTarget !== null}
        title="Delete Category"
        description={`Soft delete "${deleteTarget?.name ?? "this category"}"? It can be restored by editing the inactive category later.`}
        isPending={deleteCategory.isPending}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

function TextField({
  label,
  value,
  onChange,
  type = "text",
  required,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: "text" | "number";
  required?: boolean;
}) {
  return (
    <label className="block">
      <span className="text-sm text-content-secondary">{label}</span>
      <input
        className="mt-2 h-11 w-full rounded-md border border-cyan-300/20 bg-black/30 px-3 text-sm text-white outline-none transition focus:border-cyan-300"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        type={type}
        required={required}
      />
    </label>
  );
}
